import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  Phone, MessageSquare, Building2, MapPin, User, Mail, 
  Smartphone, Wifi, Cable, Check, AlertTriangle, Target,
  TrendingUp, Zap, Shield, Clock, Layers, Database, Copy, MailOpen,
  Edit, Send, Loader2, Save, X, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { formatarCNPJ, formatarMoeda, formatarTelefone, normalizarTelefoneStaging, parseCurrencyInput } from '@/utils/formatters';
import { ClickToCall } from '@/components/common/ClickToCall';
import { useParqueMovelByCnpj, useParqueFixoByCnpj, useCoberturaClaroByCep } from '@/hooks/useParqueVivoByCnpj';
import { useCnpjContatoMaster } from '@/hooks/useCnpjContatoMaster';
import { useMapaParqueByCnpj, useGestorVivoCnpj } from '@/hooks/useMapaParqueByCnpj';
import { useLeadDadosComplementar } from '@/hooks/useLeadDadosComplementar';
import { useOperadoraTelefone } from '@/hooks/useOperadoraTelefone';
import { OperadoraBadge } from '@/components/leads-carteira/OperadoraBadge';
import { WhatsappBadge } from '@/components/leads-carteira/WhatsappBadge';
import { mDaConta, type LinhaM } from '@/lib/mParqueVivo';
import { useMemo, useState, useCallback, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAbrirConversaInbox } from '@/hooks/useAbrirConversaInbox';
import { cn } from '@/lib/utils';
import type { LeadCarteira } from '@/hooks/useLeadsCarteira';

// CardEmailClaro foi extraído para ./CardEmailClaro.tsx

interface LeadDados360PortabilidadeProps {
  lead: LeadCarteira;
  onLigar: (telefone: string) => void;
  campanhaNome?: string | null;
}

// ── 73a · o molde das 12 caixas ────────────────────────────────────────────
// Card branco, cabeçalho 32px, pares rótulo→valor de 27px com hairline.
// As 12 peças continuam existindo — muda só a roupa (doc lead-dados-12pecas).
const CARD_73A = 'overflow-hidden rounded-xl border border-[#E7DCCB] bg-card shadow-[0_1px_2px_rgba(60,30,20,0.05),0_14px_34px_-24px_rgba(60,20,30,0.20)] dark:border-border';
const CAB_73A = 'flex h-8 flex-shrink-0 items-center gap-1.5 border-b border-[#F0E9E1] bg-[#FBFAF9] px-3.5 dark:border-border dark:bg-white/[0.02]';
const TITULO_73A = 'flex min-w-0 items-center gap-1.5 truncate text-[11px] font-bold uppercase tracking-[1.3px] text-card-foreground';
const META_73A = 'ml-auto flex flex-shrink-0 items-center gap-1.5 text-[9.5px] font-bold uppercase tracking-[0.6px] text-muted-foreground';
const CHIP_73A = 'flex h-[18px] flex-shrink-0 items-center gap-1 rounded-full bg-[#F2EEE8] px-2 text-[9px] font-bold uppercase tracking-[0.4px] text-muted-foreground dark:bg-white/[0.06]';
const GHOST_73A = 'inline-flex h-[22px] w-[22px] items-center justify-center rounded-[7px] border border-[#E4DDD3] bg-[#FAF8F5] text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary dark:border-border dark:bg-white/[0.04]';
const SOLIDO_73A = 'inline-flex h-[22px] w-[22px] items-center justify-center rounded-[7px] bg-[linear-gradient(135deg,#4A1320,#5B0A0D)] text-white shadow-[0_3px_8px_-4px_rgba(91,10,13,0.6)] transition-all hover:bg-[linear-gradient(135deg,#5B0A0D,#6E1626)] hover:brightness-110 dark:bg-[linear-gradient(135deg,#9D2530,#6C2236)]';
const FILETE_73A = 'absolute inset-y-0 left-0 w-1 bg-[linear-gradient(180deg,#6E1626,#4E1522_55%,#3E0E1B)]';

/** Par rótulo→valor de 27px com hairline — a linha padrão das caixas. */
function Par73a({ rotulo, children, money, faint }: { rotulo: string; children: ReactNode; money?: boolean; faint?: boolean }) {
  return (
    <div className="flex min-h-[27px] items-center justify-between gap-3 border-b border-[#F0E9E1] px-3.5 last:border-b-0 dark:border-border/60">
      <span className="min-w-0 truncate text-[11px] text-muted-foreground">{rotulo}</span>
      <span className={cn(
        'flex-shrink-0 truncate text-right tabular-nums',
        money ? 'font-display text-[13px] font-bold text-money-highlight' : 'text-[12px] font-bold text-card-foreground',
        faint && 'font-normal text-muted-foreground/40',
      )}>
        {children}
      </span>
    </div>
  );
}

/** Tile do molde: rótulo 9px + valor 15px Bricolage (nunca número gigante colorido). */
function Tile73a({ rotulo, children, money }: { rotulo: string; children: ReactNode; money?: boolean }) {
  return (
    <div className="min-w-0 rounded-[9px] border border-[#E7DCCB] bg-card px-3 py-2 dark:border-border">
      <p className="truncate text-[9px] font-bold uppercase tracking-[0.08em] text-muted-foreground">{rotulo}</p>
      <p className={cn(
        'mt-0.5 truncate font-display text-[15px] font-bold leading-tight tabular-nums',
        money ? 'text-money-highlight' : 'text-card-foreground',
      )}>
        {children}
      </p>
    </div>
  );
}

export function LeadDados360Portabilidade({ lead, onLigar, campanhaNome }: LeadDados360PortabilidadeProps) {
  // Abre o WhatsApp no inbox interno (Evolution) com a conexão do usuário logado
  const { abrirConversa } = useAbrirConversaInbox();

  // Extrair dados_discadora
  const dados = lead.dados_discadora as any || {};
  const dadosMaster = lead.dados_master_lead as any || {};
  
  // 🆕 Buscar dados do staging como fallback
  const { data: parqueMovel, isLoading: loadingMovel } = useParqueMovelByCnpj(lead?.cnpj);
  const { data: parqueFixo, isLoading: loadingFixo } = useParqueFixoByCnpj(lead?.cnpj);
  const { master: contatoMaster, isLoading: loadingMaster } = useCnpjContatoMaster(lead?.cnpj);
  const { mapaParque, isLoading: loadingMapa } = useMapaParqueByCnpj(lead?.cnpj);
  const { data: gestorVivo } = useGestorVivoCnpj(lead?.cnpj);
  const { data: dadosComplementar } = useLeadDadosComplementar(lead?.cnpj);

  // 🆕 Cobertura Claro pelo CEP (cruzamento Vivo x Claro) + sinais da base móvel
  const cepLead = String(mapaParque?.nr_cep || parqueMovel?.[0]?.nr_cep || (lead as any)?.cep || '');
  const { data: coberturaClaro } = useCoberturaClaroByCep(cepLead);
  const vivoTemBL = parqueMovel?.some(l => (l.tem_cobertura_banda_larga || '').toUpperCase() === 'SIM') || false;
  const aptoConvergencia = parqueMovel?.some(l => (l.cliente_apto_convergencia || '').toUpperCase() === 'SIM') || false;

  const loading = loadingMovel || loadingFixo || loadingMaster || loadingMapa;
  
  // 🆕 Calcular totais do staging
  const stagingTotals = useMemo(() => {
    const linhas = parqueMovel?.length || 0;
    const fatura = parqueMovel?.reduce((sum, l) => sum + (l.fat_medio_03_meses || 0), 0) || 0;
    const gbContratado = parqueMovel?.reduce((sum, l) => sum + (l.qtd_gb_contratado_dados || 0), 0) || 0;
    const gbTrafegado = parqueMovel?.reduce((sum, l) => sum + (l.qtd_gb_traf_dados || 0), 0) || 0;
    
    const { mInicial: mConta, mAgora: mContaAgora, linhasSemMulta, semMulta: algumaSemMulta } = mDaConta(parqueMovel as LinhaM[] | undefined);

    return { linhas, fatura, gbContratado, gbTrafegado, mConta, mContaAgora, linhasSemMulta, algumaSemMulta };
  }, [parqueMovel]);
  
  // 🆕 Verificar se tem dados no staging para FTTH/FTTC/Metálico
  const stagingTecnologia = useMemo(() => {
    const tecnologias = parqueFixo?.map(f => f.tecnologia_origem?.toUpperCase()) || [];
    return {
      ftth: tecnologias.some(t => t?.includes('FTTH')),
      fttc: tecnologias.some(t => t?.includes('FTTC')),
      metalico: tecnologias.some(t => t?.includes('METAL') || t?.includes('COBRE')),
    };
  }, [parqueFixo]);
  
  // M da CONTA: com base móvel, M inicial = maior M do arquivo e M de agora = maior (M da carga + meses desde a carga).
  // Sem base, mantém o cálculo antigo (M original + meses).
  const mOriginal = stagingTotals.mConta > 0
    ? stagingTotals.mConta
    : (parseInt(String(lead.tempo_contrato_voz || dados.mes_referencia_original || '0').replace(/\D/g, ''), 10) || 0);
  
  // 🆕 Data Consulta: usar mes_referencia como fallback (primeiro dia do mês de importação)
  let dataConsulta = dados.data_consulta;
  if (!dataConsulta && lead.mes_referencia) {
    try {
      const mesRef = new Date(lead.mes_referencia);
      const dia = String(mesRef.getDate()).padStart(2, '0');
      const mes = String(mesRef.getMonth() + 1).padStart(2, '0');
      const ano = mesRef.getFullYear();
      dataConsulta = `${dia}/${mes}/${ano}`;
    } catch {
      dataConsulta = null;
    }
  }
  
  let mesesDecorridos = 0;
  if (dataConsulta) {
    try {
      let dataConsultaDate: Date;
      if (dataConsulta.includes('/')) {
        const partes = dataConsulta.split('/');
        dataConsultaDate = new Date(parseInt(partes[2], 10), parseInt(partes[1], 10) - 1, parseInt(partes[0], 10));
      } else {
        dataConsultaDate = new Date(dataConsulta);
      }
      const hoje = new Date();
      mesesDecorridos = (hoje.getFullYear() - dataConsultaDate.getFullYear()) * 12 + (hoje.getMonth() - dataConsultaDate.getMonth());
      if (mesesDecorridos < 0) mesesDecorridos = 0;
    } catch {
      mesesDecorridos = 0;
    }
  }
  const temBaseMovel = stagingTotals.linhas > 0;
  const mAtual = temBaseMovel ? stagingTotals.mContaAgora : (mOriginal + mesesDecorridos);
  const semMulta = temBaseMovel ? stagingTotals.algumaSemMulta : mAtual >= 24;

  // Linhas da conta: a base Vivo manda; a cópia gravada no lead só vale sem base
  const linhasLead = parseInt(String(lead.qtd_linhas_voz || dados.qtd_linhas_voz || '0'), 10) || 0;
  const linhas = temBaseMovel ? stagingTotals.linhas : linhasLead;

  // Guard: leads BASICA (somente fixa) nunca mostram fatura móvel — só quando NÃO há nenhuma linha móvel (nem no staging)
  const isApenasFixa = lead.produto === 'BASICA' ||
    (linhasLead === 0 && stagingTotals.linhas === 0 && (lead.qtd_linhas_fixas != null && lead.qtd_linhas_fixas > 0));

  // Para leads apenas fixa: forçar valorFatura = 0 no Parque Móvel
  // O faturamento real aparece na seção Fixa via stagingParqueFixo
  const rawFatura = isApenasFixa ? null : (lead.valor_fatura_atual ?? dados.valor_fatura_atual);
  const valorFaturaBase = rawFatura != null
    ? (typeof rawFatura === 'number' ? rawFatura : parseCurrencyInput(String(rawFatura)))
    : 0;
  // VALOR DA CONTA: a soma real das linhas (staging) manda quando houver; senão o valor salvo; senão presumido
  const valorFatura = isApenasFixa
    ? 0
    : stagingTotals.fatura > 0
      ? stagingTotals.fatura
      : valorFaturaBase > 0
        ? valorFaturaBase
        : linhas > 0
          ? (dadosComplementar?.faturamento_presumido ? Number(dadosComplementar.faturamento_presumido) : 0)
          : 0;
  
  // Score de prioridade (usando dados reais do staging quando disponível)
  let score = 0;
  const scoreBreakdown: { label: string; pontos: number }[] = [];
  
  // 🆕 Pontuação básica por ter dados válidos
  if (linhas > 0) { score += 5; scoreBreakdown.push({ label: 'Tem Linhas', pontos: 5 }); }
  if (valorFatura > 0) { score += 5; scoreBreakdown.push({ label: 'Tem Fatura', pontos: 5 }); }
  
  if (semMulta) { score += 30; scoreBreakdown.push({ label: 'Sem Multa (M≥24)', pontos: 30 }); }
  
  if (linhas >= 20) { score += 25; scoreBreakdown.push({ label: 'Linhas 20+', pontos: 25 }); }
  else if (linhas >= 10) { score += 15; scoreBreakdown.push({ label: 'Linhas 10+', pontos: 15 }); }
  
  if (valorFatura >= 5000) { score += 25; scoreBreakdown.push({ label: 'Fatura R$5k+', pontos: 25 }); }
  else if (valorFatura >= 2000) { score += 15; scoreBreakdown.push({ label: 'Fatura R$2k+', pontos: 15 }); }

  // 🆕 Munição Claro: cobertura, dívida Vivo (argumento) e multi-filial
  if (coberturaClaro?.coberto) { score += 15; scoreBreakdown.push({ label: 'Cobertura Claro', pontos: 15 }); }
  const _divVivo = (mapaParque?.vl_car_movel || 0) + (mapaParque?.vl_car_fixa || 0);
  if (_divVivo > 0) { score += 10; scoreBreakdown.push({ label: 'Dívida Vivo (argumento)', pontos: 10 }); }
  if ((mapaParque?.qtd_sfa_filiais ?? 0) > 1) { score += 10; scoreBreakdown.push({ label: `${mapaParque.qtd_sfa_filiais} filiais`, pontos: 10 }); }

  // Tecnologia - usar dados do staging como fallback
  const temMetalico = dadosMaster.qt_basica_term_metalico > 0 || dados.qt_basica_term_metalico > 0 || stagingTecnologia.metalico || (mapaParque?.qt_basica_term_metalico ?? 0) > 0;
  const temFTTC = dadosMaster.qt_bl_fttc > 0 || dados.qt_bl_fttc > 0 || stagingTecnologia.fttc || (mapaParque?.qt_bl_fttc ?? 0) > 0;
  const temFTTH = dadosMaster.qt_bl_ftth > 0 || dados.qt_bl_ftth > 0 || stagingTecnologia.ftth || (mapaParque?.qt_bl_ftth ?? 0) > 0;
  if (temMetalico || temFTTC) { score += 20; scoreBreakdown.push({ label: 'Oport. Fibra', pontos: 20 }); }
  
  // Telefones ordenados - incluindo telefones do mapa (com normalização de formato científico)
  const telefonesMapaNormalizados = [
    normalizarTelefoneStaging(mapaParque?.tlfn_1),
    normalizarTelefoneStaging(mapaParque?.tlfn_2),
    normalizarTelefoneStaging(mapaParque?.tlfn_3),
    normalizarTelefoneStaging(mapaParque?.tlfn_4),
    normalizarTelefoneStaging(mapaParque?.tlfn_5),
    normalizarTelefoneStaging(mapaParque?.tel_comercial_siebel),
    normalizarTelefoneStaging(mapaParque?.tel_celular_siebel),
    normalizarTelefoneStaging(mapaParque?.tel_residencial_siebel),
  ].filter(Boolean) as string[];
  
  const telefones = Array.from(new Set([
    lead.contato_real_telefone || dados.celular,
    lead.telefone,
    lead.telefone_2 || dados.telefone_2,
    lead.telefone_3 || dados.telefone_3,
    lead.telefone_4 || dados.telefone_4,
    dados.telefone_5,
    ...telefonesMapaNormalizados,
  ].filter(t => t && String(t).replace(/\D/g, '').length >= 10).map(t => String(t).replace(/\D/g, ''))));
  
  // Oportunidades/Alertas
  const oportunidades: { label: string; cor: string; icon: any }[] = [];
  if (semMulta) oportunidades.push({ label: 'Sem Multa', cor: 'bg-[#075E54]', icon: Check });
  else oportunidades.push({ label: 'Com Multa', cor: 'bg-primary', icon: AlertTriangle });
  if (temMetalico || temFTTC) oportunidades.push({ label: 'Oport. Fibra', cor: 'bg-primary', icon: Wifi });
  if (linhas >= 10) oportunidades.push({ label: `${linhas} Linhas`, cor: 'bg-primary', icon: Layers });
  // Dívidas Vivo = oportunidade de portabilidade
  const _dividaFixaTemp = mapaParque?.vl_car_fixa || 0;
  const _dividaMovelTemp = mapaParque?.vl_car_movel || 0;
  if (_dividaFixaTemp > 0) oportunidades.push({ label: `Dívida Fixa ${formatarMoeda(_dividaFixaTemp)}`, cor: 'bg-primary', icon: AlertTriangle });
  if (_dividaMovelTemp > 0) oportunidades.push({ label: `Dívida Móvel ${formatarMoeda(_dividaMovelTemp)}`, cor: 'bg-primary', icon: AlertTriangle });
  
  // Subtipo
  const subTipo = lead.sub_tipo_portabilidade;

  // Indicador de fonte de dados
  const usandoStaging = stagingTotals.linhas > 0 && !dadosMaster.base_linhas_movel;

  const socioVivo = dadosMaster.origem_vivo === 'SOCIO_VIVO'
    ? (dadosMaster.socio_vivo as { nome?: string; linhas?: number; fatura?: number; desde?: string } | undefined) || null
    : null;
  
  // 🆕 Dados do Gestor/Contato Principal - cascata: master > SFA mapa > contato real > discadora
  const gestorNome = contatoMaster?.gestor_nome || gestorVivo?.gestor_nome || lead.contato_real_nome || dados.nome_completo || null;
  const gestorTelefone = contatoMaster?.gestor_telefone || gestorVivo?.gestor_celular || gestorVivo?.celular_siebel || lead.contato_real_telefone || dados.celular || lead.telefone || null;
  const gestorEmail = contatoMaster?.gestor_email || gestorVivo?.gestor_email || gestorVivo?.email_siebel || lead.contato_real_email || dados.email || lead.email || null;
  const gestorCargo = lead.contato_real_cargo || mapaParque?.papel_contato_sfa || null;
  const { data: gestorOperadora } = useOperadoraTelefone(gestorTelefone);
  const gestorEhClaro = !!gestorOperadora && /claro/i.test(gestorOperadora);

  // 🆕 Dívida Fixa e Móvel (CAR = Contas a Receber da Vivo - é dívida, não fatura)
  const dividaFixa = mapaParque?.vl_car_fixa || 0;
  const dividaMovel = mapaParque?.vl_car_movel || 0;
  
  // Faturamento real da Fixa (soma dos vl_fat_bruto do staging_parque_fixo)
  const fixaFaturamento = useMemo(() => {
    if (parqueFixo && parqueFixo.length > 0) {
      return parqueFixo.reduce((sum, f) => sum + (f.vl_fat_bruto || 0), 0);
    }
    // 🔵 Fallback: valor importado da planilha
    if (lead.valor_fatura_fixa && Number(lead.valor_fatura_fixa) > 0) {
      return Number(lead.valor_fatura_fixa);
    }
    return 0;
  }, [parqueFixo, lead.valor_fatura_fixa]);
  
  const ddFixa = (lead.dados_discadora as any) || {};
  const ddFixaLinhas = (parseInt(ddFixa.qt_basica_term_metalico || '0', 10) || 0)
                     + (parseInt(ddFixa.qt_basica_term_fibra || '0', 10) || 0);
  const fixaLinhas = parseInt(String(lead.qtd_linhas_fixas || '0'), 10) || ddFixaLinhas || mapaParque?.qt_basica_linas || parqueFixo?.length || 0;
  
  // M Original da Fixa: mapa parque > staging_parque_fixo (menor M = mais restritivo)
  const fixaMStaging = useMemo(() => {
    if (!parqueFixo || parqueFixo.length === 0) return null;
    const mValues = parqueFixo.map(f => f.m).filter((m): m is number => m !== null && m !== undefined && m > 0);
    return mValues.length > 0 ? Math.min(...mValues) : null;
  }, [parqueFixo]);
  
  const fixaM = mapaParque?.m_mapa || mapaParque?.m_consulta || fixaMStaging || null;
  
  // M Atual da Fixa: calcular independente usando dt_ini_fdlz do staging
  const fixaMAtual = useMemo(() => {
    if (!fixaM) return null;
    // Tentar calcular meses decorridos a partir de dt_ini_fdlz do staging
    if (parqueFixo && parqueFixo.length > 0) {
      const datesValid = parqueFixo
        .map(f => f.dt_ini_fdlz)
        .filter((d): d is string => !!d);
      if (datesValid.length > 0) {
        // Usar a data mais antiga (início da fidelidade mais restritiva)
        const earliest = datesValid.sort()[0];
        const inicio = new Date(earliest);
        const agora = new Date();
        const mesesDesdeInicio = (agora.getFullYear() - inicio.getFullYear()) * 12 + (agora.getMonth() - inicio.getMonth());
        return Math.max(0, mesesDesdeInicio);
      }
    }
    // Fallback: usar mesesDecorridos do móvel
    return fixaM + mesesDecorridos;
  }, [fixaM, parqueFixo, mesesDecorridos]);
  
  const temDadosFixa = fixaFaturamento > 0 || dividaFixa > 0 || fixaLinhas > 0 || fixaM
    || (lead.valor_fatura_fixa != null && Number(lead.valor_fatura_fixa) > 0)
    || (lead.qtd_linhas_fixas != null && Number(lead.qtd_linhas_fixas) > 0);

  // 🔵 Fonte do faturamento fixa: staging ou importação?
  const fixaFaturamentoIsImportado = fixaFaturamento > 0
    && (parqueFixo == null || parqueFixo.length === 0)
    && lead.valor_fatura_fixa != null && Number(lead.valor_fatura_fixa) > 0;
  
  return (
    <div className="space-y-4 p-1">
      {/* Loading state */}
      {loading && (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}

      {!loading && (
        <>
          {/* Header: Score + Dados Cliente */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* ═══ CAIXA 1 · SCORE DE PRIORIDADE ═══ */}
            <div className={CARD_73A}>
              <div className={CAB_73A}>
                <p className={TITULO_73A}>
                  <Target className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                  Score de prioridade
                </p>
                {usandoStaging && <span className={META_73A}>base Vivo</span>}
              </div>
              <div className="px-3.5 pb-1 pt-2.5">
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-[29px] font-bold leading-none tabular-nums text-card-foreground">{score}</span>
                  <span className="text-[9.5px] font-bold uppercase tracking-[0.6px] text-muted-foreground">
                    {score >= 70 ? 'alto' : score >= 40 ? 'médio' : 'baixo'}
                  </span>
                </div>
                {/* barra 5px com preenchimento degradê vinho no trilho bone */}
                <div className="mt-2 h-[5px] w-full overflow-hidden rounded-full bg-[#EAE4DA] dark:bg-white/[0.08]">
                  <div
                    className="h-full rounded-full bg-[linear-gradient(90deg,#6E1626,#4E1522)] transition-all"
                    style={{ width: `${Math.min(score, 100)}%` }}
                  />
                </div>
              </div>
              <div className="mt-1.5">
                {scoreBreakdown.map((item, i) => (
                  <Par73a key={i} rotulo={item.label}>+{item.pontos}</Par73a>
                ))}
              </div>
            </div>

            {/* ═══ CAIXA 2 · DADOS DO CLIENTE ═══ */}
            <div className={CARD_73A}>
              <div className={CAB_73A}>
                <p className={TITULO_73A}>
                  <Building2 className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                  Dados do cliente
                </p>
                <span className={META_73A}>
                  {subTipo && (
                    <span className="flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
                      {subTipo === 'CARTEIRA' ? 'Carteira' : 'Fora carteira'}
                    </span>
                  )}
                  {mapaParque?.tp_produto && <span className={CHIP_73A}>{mapaParque.tp_produto}</span>}
                </span>
              </div>
              <div className="px-3.5 pb-2 pt-2.5">
                <p className="truncate text-[12.5px] font-bold text-card-foreground" title={lead.razao_social || ''}>
                  {lead.razao_social || '—'}
                </p>
                <p className="mt-0.5 truncate text-[10.5px] tabular-nums text-muted-foreground">
                  {formatarCNPJ(lead.cnpj)}
                  {(dados.cidade || lead.uf) ? ` · ${dados.cidade || ''}${dados.cidade && lead.uf ? ' / ' : ''}${lead.uf || ''}` : ''}
                </p>
              </div>
              <div>
                {(linhas > 0 || fixaLinhas > 0) && (
                  <Par73a rotulo="Linhas">
                    {[linhas > 0 ? `${linhas} móvel` : null, fixaLinhas > 0 ? `${fixaLinhas} fixa` : null].filter(Boolean).join(' · ')}
                  </Par73a>
                )}
                {(dados.vertical || mapaParque?.vertical) && (
                  <Par73a rotulo="Vertical">{dados.vertical || mapaParque?.vertical}</Par73a>
                )}
                {mapaParque?.cluster && <Par73a rotulo="Cluster">{mapaParque.cluster}</Par73a>}
                {campanhaNome && <Par73a rotulo="Campanha">{campanhaNome}</Par73a>}
              </div>
            </div>
          </div>
          
          {/* ═══ CAIXA 3 · CONTATO PRINCIPAL (GESTOR) — o destaque da aba ═══ */}
          <div className={cn(CARD_73A, 'relative pl-1')}>
            <span aria-hidden className={FILETE_73A} />
            <div className="flex flex-wrap items-center gap-3 px-3.5 py-2.5">
              {/* avatar 42px com as iniciais */}
              <span className="grid h-[42px] w-[42px] flex-shrink-0 place-items-center rounded-full bg-[linear-gradient(135deg,#7A2235,#4A1320)] font-display text-[14px] font-bold text-white">
                {(gestorNome || '?').trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className={cn('truncate text-[13.5px] font-bold', gestorNome ? 'text-card-foreground' : 'text-muted-foreground/50')}>
                    {gestorNome || 'Não informado'}
                  </span>
                  {gestorCargo && (
                    <span className="flex h-[18px] flex-shrink-0 items-center rounded-full border border-primary/40 px-2 text-[9.5px] font-bold uppercase tracking-[0.4px] text-primary dark:text-[#C87484]">
                      {gestorCargo}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
                  <span className={cn('tabular-nums', gestorTelefone ? 'font-bold text-card-foreground' : 'text-muted-foreground/40')}>
                    {gestorTelefone ? formatarTelefone(gestorTelefone) : '—'}
                  </span>
                  {gestorOperadora && (
                    <span
                      className={CHIP_73A}
                      title={gestorEhClaro ? 'Telefone do gestor é CLARO — alvo quente de portabilidade' : `Operadora do gestor: ${gestorOperadora}`}
                    >
                      {gestorEhClaro ? 'CLARO' : gestorOperadora}
                    </span>
                  )}
                  <span className="text-muted-foreground/40">·</span>
                  <span className={cn('min-w-0 truncate', gestorEmail ? 'text-muted-foreground' : 'text-muted-foreground/40')} title={gestorEmail || undefined}>
                    {gestorEmail || '—'}
                  </span>
                  {gestorEmail && (
                    <button
                      type="button"
                      className="flex-shrink-0 text-muted-foreground/50 transition-colors hover:text-primary"
                      title="Copiar e-mail"
                      onClick={() => { navigator.clipboard.writeText(gestorEmail); toast.success('E-mail copiado'); }}
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
              {/* ações: 2 ghosts + WhatsApp sólido (a mais usada) */}
              <div className="flex flex-shrink-0 items-center gap-1">
                {gestorTelefone && (
                  <ClickToCall telefone={gestorTelefone} leadId={lead.id}>
                    <button type="button" className={GHOST_73A} title="Ligar pela discadora">
                      <Phone className="h-[11px] w-[11px]" />
                    </button>
                  </ClickToCall>
                )}
                <button
                  type="button"
                  className={cn(GHOST_73A, !gestorEmail && 'pointer-events-none opacity-40')}
                  title={gestorEmail ? `Escrever para ${gestorEmail}` : 'Sem e-mail do gestor'}
                  onClick={() => gestorEmail && window.open(`mailto:${gestorEmail}`, '_blank')}
                >
                  <Mail className="h-[11px] w-[11px]" />
                </button>
                <button
                  type="button"
                  className={cn(SOLIDO_73A, !gestorTelefone && 'pointer-events-none opacity-40')}
                  title={gestorTelefone ? 'Falar com o gestor no WhatsApp' : 'Sem telefone do gestor'}
                  onClick={() => gestorTelefone && abrirConversa(gestorTelefone, {
                    novaAba: true,
                    leadId: lead.id,
                    mensagemInicial: gestorNome
                      ? `Olá, falo com ${gestorNome}, gestor da conta empresarial da ${lead.razao_social || 'empresa'}?`
                      : `Olá, falo com o gestor da conta empresarial da ${lead.razao_social || 'empresa'}?`,
                  })}
                >
                  <MessageSquare className="h-[11px] w-[11px]" />
                </button>
              </div>
            </div>
          </div>
          
          {/* ═══ CAIXA 4 · DADOS DA CONSULTA ORIGINAL ═══ */}
          <div className={CARD_73A}>
            <div className={CAB_73A}>
              <p className={TITULO_73A}>
                <Clock className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                Dados da consulta original
              </p>
              <span className={CHIP_73A}>Móvel</span>
              {dataConsulta && <span className={cn(META_73A, 'normal-case tracking-normal')}>consulta {dataConsulta}</span>}
            </div>
            <div className="p-3 pb-2">
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                <Tile73a rotulo="M original">{mOriginal || '—'}</Tile73a>
                <Tile73a rotulo="M atual">
                  {mAtual}
                  {semMulta && <span className="ml-1.5 text-[9.5px] font-bold uppercase text-[#075E54] dark:text-[#25D366]" title="Basta uma linha sem multa para o CNPJ contar como sem multa">{temBaseMovel ? `${stagingTotals.linhasSemMulta} de ${stagingTotals.linhas} sem multa` : 'sem multa'}</span>}
                </Tile73a>
                <Tile73a rotulo="Linhas">{linhas || '—'}</Tile73a>
                <Tile73a rotulo="Valor" money>{valorFatura > 0 ? formatarMoeda(valorFatura) : '—'}</Tile73a>
              </div>
              {dividaMovel > 0 && (
                <p className="mt-2 flex items-center gap-1.5 text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#7A2235] dark:bg-[#C87484]" />
                  <span className="text-muted-foreground">Dívida móvel Vivo</span>
                  <span className="font-bold tabular-nums text-[#7A2235] dark:text-[#C87484]">{formatarMoeda(dividaMovel)}</span>
                </p>
              )}
            </div>
            {temDadosFixa && (
              <div className="border-t border-[#F0E9E1] p-3 pt-2.5 dark:border-border">
                <p className="mb-2 flex items-center gap-1.5 text-[9.5px] font-bold uppercase tracking-[0.6px] text-muted-foreground">
                  <Cable className="h-3 w-3" /> Fixa
                </p>
                <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                  <Tile73a rotulo="M original">{fixaM ?? '—'}</Tile73a>
                  <Tile73a rotulo="M atual">{fixaMAtual ?? '—'}</Tile73a>
                  <Tile73a rotulo="Linhas fixas">{fixaLinhas || '—'}</Tile73a>
                  <Tile73a rotulo={fixaFaturamentoIsImportado ? 'Faturamento · importado' : 'Faturamento'} money>
                    {fixaFaturamento > 0 ? formatarMoeda(fixaFaturamento) : '—'}
                  </Tile73a>
                </div>
                {dividaFixa > 0 && (
                  <p className="mt-2 flex items-center gap-1.5 text-[11px]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#7A2235] dark:bg-[#C87484]" />
                    <span className="text-muted-foreground">Dívida fixa Vivo</span>
                    <span className="font-bold tabular-nums text-[#7A2235] dark:text-[#C87484]">{formatarMoeda(dividaFixa)}</span>
                  </p>
                )}
              </div>
            )}
          </div>
          
          {/* Parque Móvel e Fixo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* ═══ CAIXA 5 · PARQUE MÓVEL ═══ */}
            <div className={CARD_73A}>
              <div className={CAB_73A}>
                <p className={TITULO_73A}>
                  <Smartphone className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                  Parque móvel
                </p>
                {usandoStaging && <span className={CHIP_73A}>base Vivo</span>}
              </div>
              <div>
                <Par73a rotulo="Linhas">{linhas || '—'}</Par73a>
                <Par73a rotulo="Fatura" money={valorFatura > 0} faint={valorFatura <= 0}>
                  {valorFatura > 0 ? formatarMoeda(valorFatura) : '—'}
                </Par73a>
                {stagingTotals.gbContratado > 0 && (
                  <>
                    <Par73a rotulo="GB contratado">{stagingTotals.gbContratado} GB</Par73a>
                    <Par73a rotulo="GB usado">
                      {stagingTotals.gbTrafegado} GB
                      <span className="ml-1 font-normal text-muted-foreground">
                        · {Math.round((stagingTotals.gbTrafegado / stagingTotals.gbContratado) * 100)}%
                      </span>
                    </Par73a>
                  </>
                )}
              </div>
              {/* sub-card PROPOSTA CLARO: filete inset, fundo bone, valor money */}
              <div className="p-3 pt-2.5">
                {(() => {
                  const linhasParaProposta = linhas > 0 ? linhas : fixaLinhas;
                  return (
                    <div className="relative overflow-hidden rounded-r-[9px] rounded-l-none border border-[#E7DCCB] bg-[#FBF9F6] pl-1 dark:border-border dark:bg-white/[0.02]">
                      <span aria-hidden className={FILETE_73A} />
                      <div className="flex items-center justify-between gap-3 px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-[9.5px] font-bold uppercase tracking-[0.6px] text-primary dark:text-[#C87484]">Proposta Claro</p>
                          <p className="mt-0.5 truncate text-[10.5px] text-muted-foreground">
                            {linhasParaProposta > 0 ? `${linhasParaProposta} linhas · ${linhasParaProposta * 6} GB inclusos` : 'sem linhas para simular'}
                          </p>
                        </div>
                        <span className="flex-shrink-0 font-display text-[14px] font-bold tabular-nums text-money-highlight">
                          {linhasParaProposta > 0 ? formatarMoeda(linhasParaProposta * 29.99) : '—'}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* ═══ CAIXA 6 · TECNOLOGIA FIXA — zero vira travessão ═══ */}
            <div className={CARD_73A}>
              <div className={CAB_73A}>
                <p className={TITULO_73A}>
                  <Cable className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                  Tecnologia fixa
                </p>
              </div>
              {(() => {
                const valorOuTraco = (n: unknown, marcado?: boolean) => {
                  const num = Number(n) || 0;
                  if (num > 0) return { texto: String(num), faint: false };
                  if (marcado) return { texto: 'sim', faint: false };
                  return { texto: '—', faint: true };
                };
                const linhas73a = [
                  { rotulo: 'Linha voz · metálico', ...valorOuTraco(dadosMaster.qt_basica_term_metalico || dados.qt_basica_term_metalico || mapaParque?.qt_basica_term_metalico, stagingTecnologia.metalico) },
                  { rotulo: 'Linha voz · fibra', ...valorOuTraco(dadosMaster.qt_basica_term_fibra || dados.qt_basica_term_fibra || mapaParque?.qt_basica_term_fibra) },
                  { rotulo: 'Banda larga · FTTC', ...valorOuTraco(dadosMaster.qt_bl_fttc || dados.qt_bl_fttc || mapaParque?.qt_bl_fttc, stagingTecnologia.fttc) },
                  { rotulo: 'Banda larga · FTTH', ...valorOuTraco(dadosMaster.qt_bl_ftth || dados.qt_bl_ftth || mapaParque?.qt_bl_ftth, stagingTecnologia.ftth) },
                ];
                return (
                  <>
                    <div>
                      {linhas73a.map((item) => (
                        <Par73a key={item.rotulo} rotulo={item.rotulo} faint={item.faint}>{item.texto}</Par73a>
                      ))}
                    </div>
                    <p className="px-3.5 py-2 text-[10px] text-muted-foreground/60">
                      — = nada consta na consulta · 0 vira travessão
                    </p>
                  </>
                );
              })()}
            </div>
          </div>
          
          {/* 73a — duas colunas: Telefones (7) à esquerda; Oportunidades (8),
              Cobertura (9) e O que tem na Vivo (10) empilhadas à direita */}
          <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
          {/* ═══ CAIXA 7 · TELEFONES PRINCIPAIS ═══ */}
          <div className="space-y-4">
          <div className={CARD_73A}>
            <div className={CAB_73A}>
              <p className={TITULO_73A}>
                <Phone className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                Telefones principais
              </p>
              {telefones.length > 0 && (
                <span className={cn(META_73A, 'normal-case tracking-normal')}>
                  {telefones.length} {telefones.length === 1 ? 'número' : 'números'}
                </span>
              )}
            </div>
            {telefones.length > 0 ? (
              <div>
                {telefones.slice(0, 5).map((tel, i) => (
                  <div key={i} className="flex min-h-[31px] items-center gap-2 border-b border-[#F0E9E1] px-3.5 py-1 last:border-b-0 dark:border-border/60">
                    <span className="text-[12px] font-bold tabular-nums text-card-foreground">{formatarTelefone(tel)}</span>
                    <button
                      type="button"
                      className="flex-shrink-0 text-muted-foreground/50 transition-colors hover:text-primary"
                      title="Copiar telefone"
                      onClick={() => { navigator.clipboard.writeText(tel); toast.success('Telefone copiado'); }}
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                    <WhatsappBadge telefone={tel} />
                    <OperadoraBadge telefone={tel} />
                    <span className="min-w-0 flex-1" />
                    <div className="flex flex-shrink-0 items-center gap-1">
                      <ClickToCall telefone={tel} leadId={lead.id} onClick={() => onLigar(tel)}>
                        <button type="button" className={GHOST_73A} title="Ligar pela discadora">
                          <Phone className="h-[11px] w-[11px]" />
                        </button>
                      </ClickToCall>
                      <button type="button" className={SOLIDO_73A} title="Abrir WhatsApp" onClick={() => abrirConversa(tel)}>
                        <MessageSquare className="h-[11px] w-[11px]" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="px-3.5 py-3 text-[11.5px] text-muted-foreground">Nenhum telefone cadastrado.</p>
            )}
          </div>
          </div>

          {/* coluna da direita: caixas 8, 9 e 10 empilhadas */}
          <div className="space-y-4">
          {/* ═══ CAIXA 8 · OPORTUNIDADES & ALERTAS — chips com dot, sem pill sólida ═══ */}
          <div className={CARD_73A}>
            <div className={CAB_73A}>
              <p className={TITULO_73A}>
                <Zap className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                Oportunidades &amp; alertas
              </p>
            </div>
            <div className="flex flex-wrap gap-1.5 px-3.5 py-2.5">
              {oportunidades.map((op, i) => {
                // alerta (multa/dívida) = contorno bordô; fato = chip neutro
                const alerta = op.icon === AlertTriangle;
                return (
                  <span
                    key={i}
                    className={cn(
                      'flex h-[22px] items-center gap-1.5 rounded-full px-2.5 text-[10px] font-bold',
                      alerta
                        ? 'border border-primary/40 text-primary dark:text-[#C87484]'
                        : 'bg-[#F2EEE8] text-muted-foreground dark:bg-white/[0.06]',
                    )}
                  >
                    <span className={cn(
                      'h-1.5 w-1.5 flex-shrink-0 rounded-full',
                      alerta ? 'bg-[#7A2235] dark:bg-[#C87484]' : op.cor === 'bg-[#075E54]' ? 'bg-[#075E54] dark:bg-[#25D366]' : 'bg-muted-foreground/40',
                    )} />
                    {op.label.toLowerCase()}
                  </span>
                );
              })}
            </div>
          </div>
          
          {/* CardEmailClaro removido daqui - agora renderizado no ModalDetalhesLeadCompleto para TODOS os leads */}
          
          {/* Card de Contato Principal REMOVIDO daqui - movido para após Dados do Cliente */}
          
          {/* 🆕 Cobertura Banda Larga — Claro × Vivo (oportunidade de portabilidade) */}
          {(coberturaClaro || vivoTemBL || aptoConvergencia) && (
            /* ═══ CAIXA 9 · COBERTURA — CLARO × VIVO ═══ */
            <div className={CARD_73A}>
              <div className={CAB_73A}>
                <p className={TITULO_73A}>
                  <Wifi className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                  Cobertura — Claro × Vivo
                </p>
              </div>
              <div>
                <Par73a rotulo="Vivo" faint={!vivoTemBL}>
                  {vivoTemBL ? (
                    <span className="flex items-center justify-end gap-1.5 text-[#075E54] dark:text-[#25D366]">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#075E54] dark:bg-[#25D366]" /> com cobertura
                    </span>
                  ) : '— sem cobertura'}
                </Par73a>
                <Par73a
                  rotulo={`Claro${coberturaClaro?.cep ? ` · CEP ${String(coberturaClaro.cep).replace(/\D/g, '').replace(/^(\d{5})(\d{3})$/, '$1-$2')}` : ''}`}
                  faint={!coberturaClaro?.coberto}
                >
                  {coberturaClaro?.coberto ? (
                    <span className="flex items-center justify-end gap-1.5 text-[#075E54] dark:text-[#25D366]">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#075E54] dark:bg-[#25D366]" />
                      com cobertura
                      {coberturaClaro.tipo_servico && (
                        <span className="font-normal text-muted-foreground">
                          · {coberturaClaro.tipo_servico}{coberturaClaro.velocidade_max_mbps ? ` ${coberturaClaro.velocidade_max_mbps} Mbps` : ''}
                        </span>
                      )}
                    </span>
                  ) : '— sem cobertura'}
                </Par73a>
              </div>
              {(coberturaClaro?.coberto && !vivoTemBL) || aptoConvergencia ? (
                <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-3.5 py-2 text-[10.5px] text-muted-foreground">
                  {coberturaClaro?.coberto && !vivoTemBL && (
                    <span className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#7A2235] dark:bg-[#C87484]" />
                      <span className="font-bold text-primary dark:text-[#C87484]">Oportunidade máxima</span>
                      Claro cobre e Vivo não — portabilidade + banda larga Claro
                    </span>
                  )}
                  {aptoConvergencia && <span>· cliente apto a convergência (móvel + fixa)</span>}
                </p>
              ) : null}
            </div>
          )}

          {socioVivo && (
            <div className={CARD_73A}>
              <div className={CAB_73A}>
                <p className={TITULO_73A}>
                  <User className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                  Sócio cliente Vivo
                </p>
              </div>
              <div className="space-y-2 px-3.5 py-2.5">
                <p className="text-[11.5px] leading-[1.55] text-card-foreground">
                  O sócio <b>{socioVivo.nome || gestorNome || 'administrador'}</b> é cliente Vivo no CPF
                  {socioVivo.desde ? <> desde <b>{socioVivo.desde}</b></> : null}
                  {socioVivo.linhas ? <>, com <b>{socioVivo.linhas} linha{socioVivo.linhas > 1 ? 's' : ''}</b></> : null}
                  {socioVivo.fatura ? <> e conta de <b>{formatarMoeda(socioVivo.fatura)}</b> por mês</> : null}
                  . As linhas dele estão na aba Base linhas e podem vir para o CNPJ.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  <span className={CHIP_73A}>Sócio Vivo</span>
                  {lead.cobertura_fibra === 'SIM' && <span className={CHIP_73A}>Fibra na rua da empresa</span>}
                </div>
              </div>
            </div>
          )}

          {/* 🆕 O que tem na Vivo (posse + produtos) — base p/ ação de venda Claro */}
          {(mapaParque?.posse || (mapaParque?.qt_vvn ?? 0) > 0 || (mapaParque?.qt_vivo_tech ?? 0) > 0 || mapaParque?.aparelhos || (mapaParque?.qtd_sfa_filiais ?? 0) > 0) && (
            /* ═══ CAIXA 10 · O QUE TEM NA VIVO ═══ */
            <div className={CARD_73A}>
              <div className={CAB_73A}>
                <p className={TITULO_73A}>
                  <Layers className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                  O que tem na Vivo
                </p>
              </div>
              <div className="space-y-2 px-3.5 py-2.5">
                {mapaParque?.posse && (
                  <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11.5px] leading-[1.55] text-card-foreground">
                    {mapaParque.posse}
                    {(mapaParque?.qt_movel_term ?? 0) > 0 && <span className={CHIP_73A}>{mapaParque.qt_movel_term} móvel</span>}
                  </p>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {!mapaParque?.posse && (mapaParque?.qt_movel_term ?? 0) > 0 && <span className={CHIP_73A}>{mapaParque.qt_movel_term} móvel</span>}
                  {(mapaParque?.qt_vvn ?? 0) > 0 && <span className={CHIP_73A}>VVN {mapaParque.qt_vvn}</span>}
                  {(mapaParque?.qt_basica_term_fibra ?? 0) > 0 && <span className={CHIP_73A}>Voz fibra {mapaParque.qt_basica_term_fibra}</span>}
                  {(mapaParque?.qt_basica_term_metalico ?? 0) > 0 && <span className={CHIP_73A}>Voz metálica {mapaParque.qt_basica_term_metalico}</span>}
                  {(mapaParque?.qt_basica_bl ?? 0) > 0 && <span className={CHIP_73A}>BL {mapaParque.qt_basica_bl}</span>}
                  {(mapaParque?.qt_bl_ftth ?? 0) > 0 && <span className={CHIP_73A}>BL fibra {mapaParque.qt_bl_ftth}</span>}
                  {(mapaParque?.qt_bl_fttc ?? 0) > 0 && <span className={CHIP_73A}>BL metálico {mapaParque.qt_bl_fttc}</span>}
                  {(mapaParque?.qt_avancada_dados ?? 0) > 0 && <span className={CHIP_73A}>Internet avançada {mapaParque.qt_avancada_dados}</span>}
                  {(parseInt(String(mapaParque?.avancada_voz ?? '0'), 10) || 0) > 0 && <span className={CHIP_73A}>Voz avançada {mapaParque.avancada_voz}</span>}
                  {(mapaParque?.qt_vivo_tech ?? 0) > 0 && <span className={CHIP_73A}>Vivo Tech {mapaParque.qt_vivo_tech}</span>}
                  {((mapaParque?.qt_office_365 ?? 0) > 0 || String(mapaParque?.posse || '').includes('Microsoft 365')) && <span className={CHIP_73A}>Microsoft 365{(mapaParque?.qt_office_365 ?? 0) > 0 ? ` ${mapaParque.qt_office_365}` : ''}</span>}
                  {String(mapaParque?.posse || '').includes('Google Workspace') && <span className={CHIP_73A}>Google Workspace</span>}
                  {String(mapaParque?.posse || '').includes('Outros Digitais') && <span className={CHIP_73A}>Outros digitais</span>}
                </div>
                {(mapaParque?.qtd_sfa_filiais ?? 0) > 1 && (
                  <p className="flex items-center gap-1.5 text-[10.5px] text-muted-foreground">
                    <Building2 className="h-3 w-3 flex-shrink-0" />
                    <span className="font-bold text-card-foreground">{mapaParque.qtd_sfa_filiais} filiais</span>
                    — oportunidade de pedido único p/ várias empresas
                  </p>
                )}
                {mapaParque?.aparelhos && (() => {
                  // A base manda a frase inteira ("Aparelho atual do cliente: X,
                  // oportunidade ... capacidade de pagamento de R$20880"). O card
                  // mostra só o MODELO em 700 e a capacidade em money — o texto
                  // cru repetia o rótulo e deixava o R$ sem formatação (73a).
                  const bruto = String(mapaParque.aparelhos);
                  const modelo = (bruto.match(/(?:aparelho atual do cliente|aparelho atual)\s*:\s*([^,;]+)/i)?.[1] || bruto.split(',')[0])
                    .replace(/^aparelho atual( do cliente)?\s*:\s*/i, '')
                    .trim();
                  const capacidadeDigitos = bruto.match(/capacidade de pagamento(?: de)?\s*R\$\s*([\d.,]+)/i)?.[1];
                  const capacidade = capacidadeDigitos
                    ? formatarMoeda(Number(capacidadeDigitos.replace(/\./g, '').replace(',', '.')))
                    : null;
                  return (
                    <p className="flex items-start gap-1.5 text-[10.5px] text-muted-foreground" title={bruto}>
                      <Smartphone className="mt-0.5 h-3 w-3 flex-shrink-0" />
                      <span className="min-w-0">
                        Aparelho atual: <span className="font-bold text-card-foreground">{modelo || '—'}</span>
                        {capacidade && (
                          <> — capacidade de pagamento <span className="font-bold text-money-highlight">{capacidade}</span></>
                        )}
                      </span>
                    </p>
                  );
                })()}
              </div>
            </div>
          )}
          </div>
          </div>
        </>
      )}
    </div>
  );
}
